/**
 * Sistema de auto-refresh global - SaaS-Hub
 *
 * AutoRefreshProvider  - context raiz: timer, callbacks, pausa automatica
 * useAutoRefresh(load) - regista a funcao de carga de cada pagina
 * useAutoRefreshNotify(count, label) - toast discreto quando dados mudam apos tick
 * AutoRefreshControl   - UI do topbar: anel + contador + selector + indicador de pausa
 *
 * Pausa automatica:
 *   - Quando o utilizador esta a escrever num input/textarea/select
 *   - Quando um modal esta aberto (detectado por MutationObserver via data-testid*=modal)
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { RefreshCw, PauseCircle } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "saashub_refresh_interval";
const DEFAULT_INTERVAL = 30;
const OPTIONS = [
  { label: "Off",   value: 0   },
  { label: "15s",   value: 15  },
  { label: "30s",   value: 30  },
  { label: "1 min", value: 60  },
  { label: "5 min", value: 300 },
];

const AutoRefreshContext = createContext(null);

export function AutoRefreshProvider({ children }) {
  const [refreshInterval, setRefreshIntervalState] = useState(() => {
    const s = localStorage.getItem(STORAGE_KEY);
    return s !== null ? parseInt(s, 10) : DEFAULT_INTERVAL;
  });

  // tick: incrementado cada vez que o timer dispara (auto-refresh)
  const [tick, setTick] = useState(0);

  // paused: true quando formulario activo ou modal aberto
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;

  const callbacks = useRef(new Set());

  const changeInterval = useCallback((v) => {
    localStorage.setItem(STORAGE_KEY, v);
    setRefreshIntervalState(v);
  }, []);

  const register = useCallback((fn) => {
    callbacks.current.add(fn);
    return () => callbacks.current.delete(fn);
  }, []);

  const triggerAll = useCallback(() => {
    if (pausedRef.current) return; // nao faz refresh se pausado
    callbacks.current.forEach((fn) => { try { fn(); } catch (_) {} });
    setTick((k) => k + 1);
  }, []);

  // ----- Timer principal -----
  useEffect(() => {
    if (!refreshInterval) return;
    const t = window.setInterval(triggerAll, refreshInterval * 1000);
    return () => window.clearInterval(t);
  }, [refreshInterval, triggerAll]);

  // ----- Pausa automatica: formularios -----
  useEffect(() => {
    const FORM_TAGS = ["INPUT", "TEXTAREA", "SELECT"];
    let pausedByForm = false;
    let pausedByModal = false;
    const update = () => setPaused(pausedByForm || pausedByModal);

    const onFocusIn = (e) => {
      if (FORM_TAGS.includes(e.target?.tagName)) {
        pausedByForm = true;
        update();
      }
    };
    const onFocusOut = () => {
      // pequeno delay para permitir o foco mover-se para outro campo
      window.setTimeout(() => {
        const active = document.activeElement;
        if (!active || !FORM_TAGS.includes(active.tagName)) {
          pausedByForm = false;
          update();
        }
      }, 200);
    };
    document.addEventListener("focusin",  onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    // ----- Pausa automatica: modais -----
    // Detecta abertura/fecho de modais via MutationObserver com debounce de 300ms
    // para evitar loop de feedback mutation->re-render->mutation em paginas pesadas
    const hasModal = () =>
      !!document.querySelector(
        '[data-testid*="modal"],[data-modal],[role="dialog"],.modal-overlay'
      );

    let observerTimer = null;
    const observer = new MutationObserver(() => {
      window.clearTimeout(observerTimer);
      observerTimer = window.setTimeout(() => {
        const modal = hasModal();
        if (modal !== pausedByModal) {
          pausedByModal = modal;
          update();
        }
      }, 300);
    });
    // Observa apenas childList direto do body (nao subtree) para reduzir disparo do observer
    observer.observe(document.body, { childList: true });

    return () => {
      document.removeEventListener("focusin",  onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      window.clearTimeout(observerTimer);
      observer.disconnect();
    };
  }, []);

  return (
    <AutoRefreshContext.Provider value={{ refreshInterval, changeInterval, register, triggerAll, tick, paused }}>
      {children}
    </AutoRefreshContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook: regista a funcao de carga de uma pagina no timer global
// ---------------------------------------------------------------------------
export function useAutoRefresh(loadFn) {
  const ctx = useContext(AutoRefreshContext);
  const loadRef = useRef(loadFn);
  useEffect(() => { loadRef.current = loadFn; }, [loadFn]);
  useEffect(() => {
    if (!ctx) return;
    return ctx.register(() => loadRef.current?.());
  }, [ctx]);
}

// ---------------------------------------------------------------------------
// Hook: toast discreto quando a contagem muda apos um tick de auto-refresh
//   useAutoRefreshNotify(rows.length, "tarefa(s)")
//   useAutoRefreshNotify(rows.length, "handoff(s)", { onDecrease: false })
// ---------------------------------------------------------------------------
export function useAutoRefreshNotify(count, label, { onDecrease = false } = {}) {
  const ctx = useContext(AutoRefreshContext);
  const lastTickTimeRef = useRef(null); // momento do ultimo tick
  const prevCountRef    = useRef(null); // count antes do tick
  const notifiedRef     = useRef(false); // evita toast duplicado

  // Quando tick dispara: guardar o count actual como baseline
  useEffect(() => {
    if (!ctx || ctx.tick === 0) return;
    lastTickTimeRef.current = Date.now();
    prevCountRef.current = count;
    notifiedRef.current  = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.tick]);

  // Quando count muda: verificar se e resultado de um auto-refresh recente
  useEffect(() => {
    if (notifiedRef.current) return;
    if (lastTickTimeRef.current === null) return;
    if (prevCountRef.current === null)    return;

    const elapsed = Date.now() - lastTickTimeRef.current;
    if (elapsed < 100 || elapsed > 10000) return; // so janela de 100ms a 10s

    const diff = count - prevCountRef.current;
    if (diff > 0) {
      toast(`+${diff} ${label}`, {
        duration: 4000,
        style: {
          fontSize: "12px",
          padding: "8px 14px",
          background: "#0B2545",
          color: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 12px rgba(0,0,0,.18)",
        },
      });
      notifiedRef.current = true;
    } else if (diff < 0 && onDecrease) {
      toast(`${diff} ${label}`, {
        duration: 3000,
        style: {
          fontSize: "12px",
          padding: "8px 14px",
          background: "#374151",
          color: "#fff",
          borderRadius: "8px",
        },
      });
      notifiedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
}

// ---------------------------------------------------------------------------
// Componente: controlo no topbar
// ---------------------------------------------------------------------------
export function AutoRefreshControl() {
  const ctx = useContext(AutoRefreshContext);
  const [countdown, setCountdown] = useState(ctx?.refreshInterval || 0);
  const [spinning,  setSpinning]  = useState(false);
  const [open,      setOpen]      = useState(false);
  const ref = useRef(null);
  const intervalRef = useRef(ctx?.refreshInterval || 0);

  // Reset do countdown quando intervalo muda ou tick dispara
  useEffect(() => {
    intervalRef.current = ctx?.refreshInterval || 0;
    setCountdown(ctx?.refreshInterval || 0);
  }, [ctx?.refreshInterval, ctx?.tick]);

  // Contagem regressiva local (pausa se ctx.paused)
  useEffect(() => {
    if (!ctx?.refreshInterval) { setCountdown(0); return; }
    if (ctx?.paused) return; // nao decrementar quando pausado
    const t = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) return intervalRef.current;
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [ctx?.refreshInterval, ctx?.tick, ctx?.paused]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleRefresh = () => {
    setSpinning(true);
    ctx?.triggerAll();
    setTimeout(() => setSpinning(false), 800);
  };

  if (!ctx) return null;
  const { refreshInterval, changeInterval, paused } = ctx;
  const pct    = refreshInterval > 0 ? ((refreshInterval - countdown) / refreshInterval) * 100 : 0;
  const radius = 8;
  const circ   = 2 * Math.PI * radius;
  const dash   = circ - (pct / 100) * circ;

  return (
    <div className="relative flex items-center gap-1" ref={ref} data-testid="auto-refresh-control">
      {/* Botao de refresh com anel circular */}
      <button
        onClick={handleRefresh}
        title={paused ? "Pausado (formulario/modal aberto) - clique para atualizar" : "Atualizar agora"}
        data-testid="refresh-now-btn"
        className="relative w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors group"
      >
        {refreshInterval > 0 && !paused && (
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 20 20" style={{ opacity: 0.5 }}>
            <circle cx="10" cy="10" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="1.5" />
            <circle
              cx="10" cy="10" r={radius}
              fill="none"
              stroke="var(--blue, #2563eb)"
              strokeWidth="1.5"
              strokeDasharray={circ}
              strokeDashoffset={dash}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 0.9s linear" }}
            />
          </svg>
        )}
        {paused ? (
          <PauseCircle size={14} className="text-amber-500" />
        ) : (
          <RefreshCw
            size={14}
            className={`text-slate-600 group-hover:text-navy transition-colors ${spinning ? "animate-spin" : ""}`}
          />
        )}
      </button>

      {/* Etiqueta de estado: contador ou "pausa" */}
      <button
        onClick={() => setOpen((v) => !v)}
        title="Configurar auto-refresh"
        data-testid="refresh-interval-btn"
        className="hidden sm:flex items-center gap-0.5 text-[11px] hover:text-navy transition-colors px-1 rounded hover:bg-slate-100"
        style={{ color: paused ? "#d97706" : "#64748b" }}
      >
        {paused
          ? <span className="font-semibold">pausado</span>
          : refreshInterval > 0
            ? (countdown > 0 ? `${countdown}s` : "...")
            : <span className="opacity-50">Off</span>
        }
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1"
          data-testid="refresh-interval-menu"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-100 mb-1">
            Auto-refresh
          </div>
          {OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => { changeInterval(o.value); setOpen(false); }}
              data-testid={`refresh-opt-${o.value}`}
              className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center justify-between hover:bg-slate-50 transition-colors ${refreshInterval === o.value ? "text-blue font-semibold" : "text-slate-700"}`}
            >
              {o.label}
              {refreshInterval === o.value && <span className="w-1.5 h-1.5 rounded-full bg-blue" />}
            </button>
          ))}
          {paused && (
            <div className="px-3 py-1.5 text-[10px] text-amber-600 border-t border-slate-100 mt-1 flex items-center gap-1">
              <PauseCircle size={10} /> Pausado: formulario/modal aberto
            </div>
          )}
        </div>
      )}
    </div>
  );
}
